import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  AdminCreateUserBody,
  AdminUpdateUserBody,
  User,
  UserRole,
} from '@kome/shared';
import { formatListTime, isValidPhone, normalizePhone } from '@kome/shared';
import adminApi, { AdminApiError } from '@/lib/api';
import TopBar from '@/components/TopBar';
import Card from '@/components/Card';
import Button from '@/components/Button';
import Input from '@/components/Input';
import Field from '@/components/Field';
import Badge from '@/components/Badge';
import Modal from '@/components/Modal';
import ConfirmDialog from '@/components/ConfirmDialog';
import DataTable from '@/components/DataTable';
import type { Column } from '@/components/DataTable';

/** 한 페이지에 표시할 사용자 수 */
const PAGE_SIZE = 20;
/** 검색어 입력 디바운스 (ms) */
const SEARCH_DEBOUNCE_MS = 300;
/** 비밀번호 최소 길이 (계정 생성/재설정 공통) */
const MIN_PASSWORD_LENGTH = 6;

/** 화면 상단에 잠깐 노출되는 피드백 배너 */
interface Toast {
  kind: 'success' | 'error';
  text: string;
}

/** 계정 생성 폼 상태 */
interface CreateForm {
  phone: string;
  name: string;
  password: string;
  department: string;
  position: string;
  role: UserRole;
}

const EMPTY_CREATE_FORM: CreateForm = {
  phone: '',
  name: '',
  password: '',
  department: '',
  position: '',
  role: 'member',
};

/** 계정 수정 폼 상태 (부서/직책/활성/역할) */
interface EditForm {
  department: string;
  position: string;
  role: UserRole;
  isActive: boolean;
}

/** API/네트워크 오류를 사용자용 한국어 문구로 변환 */
function messageFromError(err: unknown, fallback: string): string {
  if (err instanceof AdminApiError) {
    if (err.status === 0) return '서버에 연결할 수 없습니다. 네트워크를 확인해 주세요.';
    if (err.status === 409) return '이미 등록된 전화번호입니다.';
    return err.message || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

export default function Users() {
  // 목록 상태
  const [rows, setRows] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // 검색
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  // 피드백 배너
  const [toast, setToast] = useState<Toast | null>(null);

  // 모달 상태
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [toggleTarget, setToggleTarget] = useState<User | null>(null);

  const mountedRef = useRef(true);
  const toastTimerRef = useRef<number | null>(null);

  // ───────────────────────── 피드백 배너 표시
  const showToast = useCallback((kind: Toast['kind'], text: string) => {
    if (!mountedRef.current) return;
    setToast({ kind, text });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      if (mountedRef.current) setToast(null);
    }, 4000);
  }, []);

  // ───────────────────────── 목록 로드
  const load = useCallback(
    async (opts: { page: number; q: string }) => {
      setLoading(true);
      setListError(null);
      try {
        const res = await adminApi.users.list({
          q: opts.q || undefined,
          limit: PAGE_SIZE,
          offset: opts.page * PAGE_SIZE,
        });
        if (!mountedRef.current) return;
        setRows(res.users);
        setTotal(res.total);
      } catch (e) {
        if (!mountedRef.current) return;
        // 401 은 api 클라이언트가 세션을 폐기 → 가드가 로그인으로 보냄
        if (e instanceof AdminApiError && e.status === 401) return;
        setListError(messageFromError(e, '사용자 목록을 불러오지 못했습니다.'));
        setRows([]);
        setTotal(0);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [],
  );

  // 페이지/검색어 변경 시 로드
  useEffect(() => {
    void load({ page, q: query });
  }, [load, page, query]);

  // 검색어 디바운스 → query 반영 + 첫 페이지로
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery((prev) => {
        const next = searchInput.trim();
        if (next === prev) return prev;
        setPage(0);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // 마운트/언마운트 추적
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  /** 작업 성공 후 현재 페이지를 다시 불러온다 */
  const reload = useCallback(() => {
    void load({ page, q: query });
  }, [load, page, query]);

  // ───────────────────────── 활성 토글 (확인 다이얼로그 경유)
  const [toggleLoading, setToggleLoading] = useState(false);
  const confirmToggle = useCallback(async () => {
    if (!toggleTarget) return;
    setToggleLoading(true);
    try {
      await adminApi.users.update(toggleTarget.id, { isActive: !toggleTarget.isActive });
      if (!mountedRef.current) return;
      showToast(
        'success',
        `${toggleTarget.name} 계정을 ${toggleTarget.isActive ? '비활성화' : '활성화'}했습니다.`,
      );
      setToggleTarget(null);
      reload();
    } catch (e) {
      if (e instanceof AdminApiError && e.status === 401) return;
      showToast('error', messageFromError(e, '상태 변경에 실패했습니다.'));
    } finally {
      if (mountedRef.current) setToggleLoading(false);
    }
  }, [toggleTarget, showToast, reload]);

  // ───────────────────────── 테이블 컬럼 정의
  const columns = useMemo<Column<User>[]>(
    () => [
      {
        key: 'name',
        header: '이름',
        render: (u) => (
          <div className="user-cell">
            <span className="user-cell__name">{u.name}</span>
            {u.role === 'admin' && <Badge tone="info">관리자</Badge>}
          </div>
        ),
      },
      { key: 'phone', header: '전화번호', render: (u) => <span className="muted">{u.phone}</span> },
      {
        key: 'department',
        header: '부서',
        render: (u) => u.department || <span className="muted">—</span>,
      },
      {
        key: 'position',
        header: '직책',
        render: (u) => u.position || <span className="muted">—</span>,
      },
      {
        key: 'isActive',
        header: '상태',
        render: (u) =>
          u.isActive ? <Badge tone="success">활성</Badge> : <Badge tone="danger">비활성</Badge>,
      },
      {
        key: 'lastSeenAt',
        header: '최근 접속',
        render: (u) =>
          u.lastSeenAt ? (
            <span className="muted">{formatListTime(u.lastSeenAt)}</span>
          ) : (
            <span className="muted">없음</span>
          ),
      },
      {
        key: '_actions',
        header: '',
        sortable: false,
        width: 1,
        render: (u) => (
          <div className="row user-actions" onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="secondary" onClick={() => setEditTarget(u)}>
              수정
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)}>
              비번 재설정
            </Button>
            <Button
              size="sm"
              variant={u.isActive ? 'danger' : 'primary'}
              onClick={() => setToggleTarget(u)}
            >
              {u.isActive ? '비활성화' : '활성화'}
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <TopBar
        title="사용자"
        subtitle="계정 생성·수정·비밀번호 재설정"
        actions={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            + 계정 생성
          </Button>
        }
      />

      <div className="admin-content">
        {toast && (
          <div className={`page-toast page-toast--${toast.kind}`} role="status">
            {toast.text}
          </div>
        )}

        <div className="page-toolbar">
          <Input
            type="search"
            placeholder="이름 또는 전화번호 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 320 }}
            aria-label="사용자 검색"
          />
          <span className="muted">총 {total.toLocaleString('ko-KR')}명</span>
        </div>

        <Card noPadding>
          {listError ? (
            <div style={{ padding: 'var(--space-xl)' }}>
              <div className="muted" style={{ marginBottom: 'var(--space-md)' }}>
                {listError}
              </div>
              <Button variant="primary" onClick={reload}>
                다시 시도
              </Button>
            </div>
          ) : (
            <DataTable<User>
              columns={columns}
              rows={rows}
              rowKey={(u) => u.id}
              loading={loading}
              emptyText={query ? '검색 결과가 없습니다.' : '등록된 사용자가 없습니다.'}
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          )}
        </Card>
      </div>

      {/* 계정 생성 모달 */}
      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(user) => {
          setCreateOpen(false);
          showToast('success', `${user.name} 계정을 생성했습니다.`);
          // 새 계정을 즉시 보여주기 위해 첫 페이지로 이동(이미 0이면 강제 리로드)
          if (page === 0) reload();
          else setPage(0);
        }}
        onError={(msg) => showToast('error', msg)}
      />

      {/* 계정 수정 모달 */}
      <EditUserModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={(user) => {
          setEditTarget(null);
          showToast('success', `${user.name} 정보를 수정했습니다.`);
          reload();
        }}
        onError={(msg) => showToast('error', msg)}
      />

      {/* 비밀번호 재설정 모달 */}
      <ResetPasswordModal
        target={resetTarget}
        onClose={() => setResetTarget(null)}
        onDone={(user) => {
          setResetTarget(null);
          showToast('success', `${user.name} 비밀번호를 재설정했습니다.`);
        }}
        onError={(msg) => showToast('error', msg)}
      />

      {/* 활성 토글 확인 */}
      <ConfirmDialog
        open={!!toggleTarget}
        title={toggleTarget?.isActive ? '계정 비활성화' : '계정 활성화'}
        message={
          toggleTarget
            ? toggleTarget.isActive
              ? `${toggleTarget.name} 계정을 비활성화하면 로그인할 수 없습니다. 진행할까요?`
              : `${toggleTarget.name} 계정을 다시 활성화할까요?`
            : ''
        }
        confirmText={toggleTarget?.isActive ? '비활성화' : '활성화'}
        danger={toggleTarget?.isActive}
        loading={toggleLoading}
        onConfirm={() => void confirmToggle()}
        onCancel={() => setToggleTarget(null)}
      />

      {/* 페이지 전용 보조 스타일 */}
      <style>{`
        .page-toast {
          padding: 10px var(--space-lg);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-lg);
          font-size: var(--font-size-sm);
          border: 1px solid transparent;
        }
        .page-toast--success {
          background: #E9F8EE;
          color: #1B7A3D;
          border-color: #BCE6C9;
        }
        .page-toast--error {
          background: #FDECEC;
          color: #C0322B;
          border-color: #F4C7C4;
        }
        .user-cell {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }
        .user-cell__name {
          font-weight: 500;
        }
        .user-actions {
          justify-content: flex-end;
          white-space: nowrap;
        }
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-md);
        }
        .form-grid .field--full {
          grid-column: 1 / -1;
        }
        .role-select {
          width: 100%;
          height: 38px;
          padding: 0 var(--space-md);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          background: var(--color-surface);
          font-size: var(--font-size-md);
          font-family: inherit;
          color: var(--color-text-primary);
        }
        .check-row {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          font-size: var(--font-size-md);
        }
        .check-row input {
          width: 18px;
          height: 18px;
        }
      `}</style>
    </>
  );
}

// ════════════════════════════════════════════════════ 계정 생성 모달

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (user: User) => void;
  onError: (message: string) => void;
}

function CreateUserModal({ open, onClose, onCreated, onError }: CreateUserModalProps) {
  const [form, setForm] = useState<CreateForm>(EMPTY_CREATE_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateForm, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  // 모달이 열릴 때마다 폼 초기화
  useEffect(() => {
    if (open) {
      setForm(EMPTY_CREATE_FORM);
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  const set = <K extends keyof CreateForm>(key: K, value: CreateForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  /** 제출 전 필드 검증 */
  const validate = (): boolean => {
    const next: Partial<Record<keyof CreateForm, string>> = {};
    if (!form.phone.trim()) next.phone = '전화번호를 입력해 주세요.';
    else if (!isValidPhone(form.phone)) next.phone = '올바른 전화번호 형식이 아닙니다.';
    if (!form.name.trim()) next.name = '이름을 입력해 주세요.';
    if (!form.password) next.password = '비밀번호를 입력해 주세요.';
    else if (form.password.length < MIN_PASSWORD_LENGTH)
      next.password = `비밀번호는 ${MIN_PASSWORD_LENGTH}자 이상이어야 합니다.`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    const body: AdminCreateUserBody = {
      phone: normalizePhone(form.phone),
      name: form.name.trim(),
      password: form.password,
      department: form.department.trim() || undefined,
      position: form.position.trim() || undefined,
      role: form.role,
    };
    try {
      const user = await adminApi.users.create(body);
      onCreated(user);
    } catch (err) {
      if (err instanceof AdminApiError && err.status === 401) return;
      // 전화번호 충돌은 인라인 에러로도 표시
      if (err instanceof AdminApiError && err.status === 409) {
        setErrors((prev) => ({ ...prev, phone: '이미 등록된 전화번호입니다.' }));
      }
      onError(messageFromError(err, '계정 생성에 실패했습니다.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="계정 생성"
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button variant="primary" form="create-user-form" type="submit" loading={submitting}>
            생성
          </Button>
        </>
      }
    >
      <form id="create-user-form" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <Field
            label="전화번호"
            htmlFor="cu-phone"
            error={errors.phone}
            hint="로그인 ID로 사용됩니다."
          >
            <Input
              id="cu-phone"
              type="tel"
              inputMode="tel"
              placeholder="010-1234-5678"
              autoFocus
              value={form.phone}
              invalid={!!errors.phone}
              disabled={submitting}
              onChange={(e) => set('phone', e.target.value)}
            />
          </Field>

          <Field label="이름" htmlFor="cu-name" error={errors.name}>
            <Input
              id="cu-name"
              placeholder="홍길동"
              value={form.name}
              invalid={!!errors.name}
              disabled={submitting}
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>

          <Field
            label="비밀번호"
            htmlFor="cu-password"
            error={errors.password}
            hint={`${MIN_PASSWORD_LENGTH}자 이상`}
          >
            <Input
              id="cu-password"
              type="password"
              autoComplete="new-password"
              placeholder="초기 비밀번호"
              value={form.password}
              invalid={!!errors.password}
              disabled={submitting}
              onChange={(e) => set('password', e.target.value)}
            />
          </Field>

          <Field label="역할" htmlFor="cu-role">
            <select
              id="cu-role"
              className="role-select"
              value={form.role}
              disabled={submitting}
              onChange={(e) => set('role', e.target.value as UserRole)}
            >
              <option value="member">일반</option>
              <option value="admin">관리자</option>
            </select>
          </Field>

          <Field label="부서" htmlFor="cu-department">
            <Input
              id="cu-department"
              placeholder="예: 개발팀"
              value={form.department}
              disabled={submitting}
              onChange={(e) => set('department', e.target.value)}
            />
          </Field>

          <Field label="직책" htmlFor="cu-position">
            <Input
              id="cu-position"
              placeholder="예: 팀장"
              value={form.position}
              disabled={submitting}
              onChange={(e) => set('position', e.target.value)}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

// ════════════════════════════════════════════════════ 계정 수정 모달

interface EditUserModalProps {
  target: User | null;
  onClose: () => void;
  onUpdated: (user: User) => void;
  onError: (message: string) => void;
}

function EditUserModal({ target, onClose, onUpdated, onError }: EditUserModalProps) {
  const [form, setForm] = useState<EditForm>({
    department: '',
    position: '',
    role: 'member',
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);

  // 수정 대상이 바뀌면 폼을 그 값으로 채운다
  useEffect(() => {
    if (target) {
      setForm({
        department: target.department ?? '',
        position: target.position ?? '',
        role: target.role,
        isActive: target.isActive,
      });
      setSubmitting(false);
    }
  }, [target]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target || submitting) return;
    setSubmitting(true);
    // 변경된 필드만 추려서 PATCH (불필요한 변경 방지)
    const body: AdminUpdateUserBody = {};
    const dept = form.department.trim();
    const pos = form.position.trim();
    if (dept !== (target.department ?? '')) body.department = dept;
    if (pos !== (target.position ?? '')) body.position = pos;
    if (form.role !== target.role) body.role = form.role;
    if (form.isActive !== target.isActive) body.isActive = form.isActive;

    // 변경 사항이 없으면 호출 없이 닫는다
    if (Object.keys(body).length === 0) {
      setSubmitting(false);
      onClose();
      return;
    }

    try {
      const user = await adminApi.users.update(target.id, body);
      onUpdated(user);
    } catch (err) {
      if (err instanceof AdminApiError && err.status === 401) return;
      onError(messageFromError(err, '계정 수정에 실패했습니다.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={!!target}
      title={target ? `${target.name} 정보 수정` : '정보 수정'}
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button variant="primary" form="edit-user-form" type="submit" loading={submitting}>
            저장
          </Button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={onSubmit} noValidate>
        <div className="form-grid">
          <Field label="부서" htmlFor="eu-department">
            <Input
              id="eu-department"
              placeholder="예: 개발팀"
              value={form.department}
              disabled={submitting}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
            />
          </Field>

          <Field label="직책" htmlFor="eu-position">
            <Input
              id="eu-position"
              placeholder="예: 팀장"
              value={form.position}
              disabled={submitting}
              onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
            />
          </Field>

          <Field label="역할" htmlFor="eu-role">
            <select
              id="eu-role"
              className="role-select"
              value={form.role}
              disabled={submitting}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
            >
              <option value="member">일반</option>
              <option value="admin">관리자</option>
            </select>
          </Field>

          <Field label="계정 상태">
            <label className="check-row">
              <input
                type="checkbox"
                checked={form.isActive}
                disabled={submitting}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              활성 (체크 해제 시 로그인 차단)
            </label>
          </Field>
        </div>
      </form>
    </Modal>
  );
}

// ════════════════════════════════════════════════════ 비밀번호 재설정 모달

interface ResetPasswordModalProps {
  target: User | null;
  onClose: () => void;
  onDone: (user: User) => void;
  onError: (message: string) => void;
}

function ResetPasswordModal({ target, onClose, onDone, onError }: ResetPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 대상이 바뀌면 입력 초기화
  useEffect(() => {
    if (target) {
      setPassword('');
      setConfirm('');
      setError(null);
      setSubmitting(false);
    }
  }, [target]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target || submitting) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`비밀번호는 ${MIN_PASSWORD_LENGTH}자 이상이어야 합니다.`);
      return;
    }
    if (password !== confirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.users.resetPassword(target.id, password);
      onDone(target);
    } catch (err) {
      if (err instanceof AdminApiError && err.status === 401) return;
      onError(messageFromError(err, '비밀번호 재설정에 실패했습니다.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={!!target}
      title={target ? `${target.name} 비밀번호 재설정` : '비밀번호 재설정'}
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button variant="primary" form="reset-pw-form" type="submit" loading={submitting}>
            재설정
          </Button>
        </>
      }
    >
      <form id="reset-pw-form" onSubmit={onSubmit} noValidate>
        <Field
          label="새 비밀번호"
          htmlFor="rp-password"
          error={error ?? undefined}
          hint={`${MIN_PASSWORD_LENGTH}자 이상`}
        >
          <Input
            id="rp-password"
            type="password"
            autoComplete="new-password"
            autoFocus
            placeholder="새 비밀번호"
            value={password}
            invalid={!!error}
            disabled={submitting}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError(null);
            }}
          />
        </Field>

        <Field label="비밀번호 확인" htmlFor="rp-confirm">
          <Input
            id="rp-confirm"
            type="password"
            autoComplete="new-password"
            placeholder="다시 입력"
            value={confirm}
            invalid={!!error}
            disabled={submitting}
            onChange={(e) => {
              setConfirm(e.target.value);
              if (error) setError(null);
            }}
          />
        </Field>
      </form>
    </Modal>
  );
}
